import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { MAX_CAROUSEL_ITEM_COUNT } from 'home/components/OfferCarousels/common/constants'
import { getPlaceKey } from 'lib/geo/geoUtils'
import { buildSearchParamsFromFilters } from 'lib/search/searchUtils'
import { queryKeyDestinationId, queryKeyDestinationName } from 'constants/url'

/** Latest hotel destination search considered for the homepage module. */
export const RECENT_SEARCH_HOTEL_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000

/** Sparse LPC/Bedbank stays of 1–2 offers still render. */
export const RECENT_SEARCH_HOTEL_MIN_OFFERS = 1

export const RECENT_SEARCH_HOTEL_OFFER_TYPES: Array<App.OfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
]

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

export type RecentSearchHotelCarouselReason =
  | 'not_logged_in'
  | 'searches_loading'
  | 'orders_loading'
  | 'audience_mismatch'
  | 'stale_search'
  | 'past_dates'
  | 'invalid_dates'
  | 'unsupported_search'
  | 'no_recent_search'
  | 'fulfilled'
  | 'fulfilled_unknown'
  | 'no_inventory'
  | 'inventory_pending'
  | 'inventory_error'
  | 'data_error'
  | 'eligible'

export type RecentSearchHotelCarouselStatus = 'loading' | 'eligible' | 'suppressed'

export interface RecentSearchHotelStay {
  countryCode?: string
  coordinates?: { latitude: number, longitude: number }
  /** Accommodation property used when the order has no country or coordinates. */
  propertyId?: string
  checkIn: string
  checkOut: string
}

export type RecentSearchHotelPropertyCountry =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready', countryCode?: string, coordinates?: { latitude: number, longitude: number } }

export interface RecentSearchHotelPlaceLookup {
  latitude: number
  longitude: number
  level: App.PlaceType
}

export interface RecentSearchHotelPurchaseSummary {
  /** Completed order whose hotel stays can hide the carousel. Refunded and cancelled orders are excluded. */
  countsAsPurchase: boolean
  stays: Array<RecentSearchHotelStay>
}

export interface RecentSearchHotelContext {
  /**
   * Deterministic correlation for this search snapshot.
   * It is not the source event id; search responses do not include one.
   */
  correlationKey: string
  searchedAt: string
  query: string
  placeIds: Array<string>
  checkIn?: string
  checkOut?: string
  rooms: Array<App.Occupants>
  dateMode: 'specific' | 'anytime' | 'flexible'
}

export interface RecentSearchHotelCarouselDecision {
  status: RecentSearchHotelCarouselStatus
  reason: RecentSearchHotelCarouselReason
  context?: RecentSearchHotelContext
  /** Property ids that still need country or coordinate data before a fulfilled decision. */
  unresolvedPropertyIds: Array<string>
  unresolvedBookingLocations: Array<RecentSearchHotelPlaceLookup>
  /** Offer ids that passed the availability check used for eligibility. */
  verifiedOfferIds: Array<string>
}

export interface RecentSearchHotelInventoryEvidence {
  enabled: boolean
  fetching: boolean
  streamEnded: boolean
  error: boolean
  offers: Array<{ id: string, available: boolean | undefined }>
}

interface EvaluateRecentSearchHotelCarouselInput {
  memberId?: string
  searches: {
    state: 'initial' | 'loading' | 'done' | 'error' | string
    cacheKey?: string
    data: Array<App.UserRecentSearch>
  }
  ordersFetched: boolean
  /** The full-history fetch finished without `ordersFetched`. Upcoming orders are not this signal. */
  ordersFailed?: boolean
  purchases: Array<RecentSearchHotelPurchaseSummary>
  propertyCountries?: Record<string, RecentSearchHotelPropertyCountry | undefined>
  /** Uses the same localized place cache already loaded for the carousel title. */
  searchPlaces: Record<string, App.PlaceCacheEntry | undefined>
  bookingPlacesByCoords?: Record<string, App.Place | null | undefined>
  now: number
  /** Calendar date `YYYY-MM-DD` in the customer's timezone. */
  today: string
}

/** Stable result while the homepage module is not in the experiment. */
export const DISABLED_RECENT_SEARCH_HOTEL_DECISION: RecentSearchHotelCarouselDecision = {
  status: 'suppressed',
  reason: 'audience_mismatch',
  unresolvedPropertyIds: [],
  unresolvedBookingLocations: [],
  verifiedOfferIds: [],
}

function decision(
  status: RecentSearchHotelCarouselStatus,
  reason: RecentSearchHotelCarouselReason,
  context?: RecentSearchHotelContext,
  unresolvedPropertyIds: Array<string> = [],
  unresolvedBookingLocations: Array<RecentSearchHotelPlaceLookup> = [],
): RecentSearchHotelCarouselDecision {
  return {
    status,
    reason,
    context,
    unresolvedPropertyIds,
    unresolvedBookingLocations,
    verifiedOfferIds: [],
  }
}

/** Re-read the clock at the search expiry, and at least once a minute for the calendar date. */
export function getRecentSearchHotelClockDelay(now: number, searchedAt?: string): number {
  const minute = 60_000
  if (!searchedAt) return minute
  const searchedAtMs = Date.parse(searchedAt)
  if (Number.isNaN(searchedAtMs)) return minute
  const untilExpiry = searchedAtMs + RECENT_SEARCH_HOTEL_LOOKBACK_MS - now
  if (untilExpiry <= 0) return minute
  return Math.min(minute, untilExpiry)
}

export function excludeRecentSearchHotelOffers(
  offerIds: Array<string>,
  excludedIds: Array<string>,
): Array<string> {
  if (excludedIds.length === 0) return offerIds
  const excluded = new Set(excludedIds)
  return offerIds.filter(offerId => !excluded.has(offerId))
}

export function isCalendarDate(value: string | undefined): value is string {
  if (!value) return false
  const match = CALENDAR_DATE.exec(value)
  if (!match) return false
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const utc = new Date(Date.UTC(year, month - 1, day))
  return utc.getUTCFullYear() === year && utc.getUTCMonth() === month - 1 && utc.getUTCDate() === day
}

function parseSearchTimestamp(timestamp: string): number | undefined {
  if (!timestamp.includes('T')) return undefined
  const parsed = Date.parse(timestamp)
  return Number.isNaN(parsed) ? undefined : parsed
}

function mapRooms(occupancy: Array<App.SearchOccupancy> | undefined): Array<App.Occupants> | undefined {
  if (!occupancy?.length) return undefined
  const rooms = occupancy.map((room): App.Occupants => ({
    adults: room.numberOfAdults,
    children: room.agesOfChildren?.length ?? 0,
    childrenAge: room.agesOfChildren ?? [],
  }))
  if (rooms.some(room => room.adults < 1)) return undefined
  return rooms
}

function uniquePlaceIds(placeIds: Array<string> | undefined): Array<string> {
  return Array.from(new Set((placeIds ?? []).filter(placeId => placeId.length > 0)))
}

/**
 * Correlation key for analytics joins. Collisions are possible across identical
 * search snapshots; do not treat this as the original search event id.
 */
export function buildRecentSearchCorrelationKey(
  search: App.UserRecentSearch,
  placeIds: Array<string>,
  rooms: Array<App.Occupants>,
): string {
  const dates = search.travelDates.type === 'specific'
    ? `${search.travelDates.checkIn ?? ''}|${search.travelDates.checkOut ?? ''}`
    : search.travelDates.type
  const occupancy = rooms
    .map(room => `${room.adults}:${(room.childrenAge ?? []).join('.')}`)
    .join(';')
  return [search.timestamp, placeIds.join(','), dates, occupancy].join('#')
}

function compareCalendarDates(left: string, right: string): number {
  if (left === right) return 0
  return left < right ? -1 : 1
}

type ClassifiedSearch =
  | { kind: 'candidate', context: RecentSearchHotelContext }
  | { kind: 'suppressed', reason: RecentSearchHotelCarouselReason, context?: RecentSearchHotelContext }

function classifySearch(search: App.UserRecentSearch, today: string): ClassifiedSearch | undefined {
  const placeIds = uniquePlaceIds(search.placeIds)
  const rooms = mapRooms(search.occupancy)
  if (search.type !== 'place' || placeIds.length === 0 || !rooms) return undefined

  const contextBase = {
    correlationKey: buildRecentSearchCorrelationKey(search, placeIds, rooms),
    searchedAt: search.timestamp,
    query: search.query,
    placeIds,
    rooms,
  }
  const travelDates = search.travelDates

  if (travelDates.type === 'anytime') {
    return { kind: 'candidate', context: { ...contextBase, dateMode: 'anytime' } }
  }

  if (travelDates.type === 'flexible') {
    return {
      kind: 'suppressed',
      reason: 'unsupported_search',
      context: { ...contextBase, dateMode: 'flexible' },
    }
  }

  if (travelDates.type !== 'specific') {
    return { kind: 'suppressed', reason: 'unsupported_search' }
  }

  const { checkIn, checkOut } = travelDates
  if (!checkIn || !checkOut) {
    return { kind: 'suppressed', reason: 'unsupported_search', context: { ...contextBase, dateMode: 'specific' } }
  }

  if (!isCalendarDate(checkIn) || !isCalendarDate(checkOut) || compareCalendarDates(checkOut, checkIn) <= 0) {
    return {
      kind: 'suppressed',
      reason: 'invalid_dates',
      context: { ...contextBase, dateMode: 'specific', checkIn, checkOut },
    }
  }

  const context: RecentSearchHotelContext = {
    ...contextBase,
    dateMode: 'specific',
    checkIn,
    checkOut,
  }

  if (compareCalendarDates(checkIn, today) < 0) {
    return { kind: 'suppressed', reason: 'past_dates', context }
  }

  return { kind: 'candidate', context }
}

function selectLatestSearch(
  searches: Array<App.UserRecentSearch>,
  now: number,
  today: string,
): ClassifiedSearch | { kind: 'none', reason: 'stale_search' | 'no_recent_search' } {
  const sorted = searches.toSorted((left, right) => {
    const leftTime = parseSearchTimestamp(left.timestamp) ?? 0
    const rightTime = parseSearchTimestamp(right.timestamp) ?? 0
    return rightTime - leftTime
  })

  let sawStalePlaceSearch = false

  for (const search of sorted) {
    const timestamp = parseSearchTimestamp(search.timestamp)
    const placeIds = uniquePlaceIds(search.placeIds)
    const isPlaceSearch = search.type === 'place' && placeIds.length > 0
    if (timestamp === undefined || !isPlaceSearch) continue
    if (timestamp > now || timestamp < now - RECENT_SEARCH_HOTEL_LOOKBACK_MS) {
      if (timestamp <= now) sawStalePlaceSearch = true
      continue
    }

    return classifySearch(search, today) ?? { kind: 'suppressed', reason: 'unsupported_search' }
  }

  return { kind: 'none', reason: sawStalePlaceSearch ? 'stale_search' : 'no_recent_search' }
}

export function normalizeRecentSearchHotelCountryCode(countryCode?: string): string | undefined {
  const normalized = countryCode?.trim().toUpperCase()
  return normalized && /^[A-Z]{2}$/.test(normalized) ? normalized : undefined
}

export function getRecentSearchHotelCoordinates(latitude?: number, longitude?: number): RecentSearchHotelStay['coordinates'] {
  if (latitude === undefined || longitude === undefined
    || !Number.isFinite(latitude) || !Number.isFinite(longitude)
    || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return undefined
  return { latitude, longitude }
}

/**
 * A matching hotel stay hides the carousel only while it is still an upcoming escape.
 * Checkout on or after today covers a future stay and a stay already in progress.
 * Completed stays do not hide it.
 */
function stayIsUpcoming(stay: RecentSearchHotelStay, today: string): boolean {
  if (!isCalendarDate(stay.checkIn) || !isCalendarDate(stay.checkOut)) return false
  if (compareCalendarDates(stay.checkOut, stay.checkIn) <= 0) return false
  return compareCalendarDates(stay.checkOut, today) >= 0
}

function pushUnique(ids: Array<string>, id: string) {
  if (!ids.includes(id)) ids.push(id)
}

function evaluateFulfilled(
  context: RecentSearchHotelContext,
  purchases: Array<RecentSearchHotelPurchaseSummary>,
  searchPlaces: Record<string, App.PlaceCacheEntry | undefined>,
  propertyCountries: Record<string, RecentSearchHotelPropertyCountry | undefined>,
  bookingPlacesByCoords: Record<string, App.Place | null | undefined>,
  today: string,
): {
  fulfilled: boolean
  pending: boolean
  unresolvedPropertyIds: Array<string>
  unresolvedBookingLocations: Array<RecentSearchHotelPlaceLookup>
} {
  const unresolvedPropertyIds: Array<string> = []
  const unresolvedBookingLocations = new Map<string, RecentSearchHotelPlaceLookup>()
  let pending = false

  for (const purchase of purchases) {
    if (!purchase.countsAsPurchase) continue
    for (const stay of purchase.stays) {
      if (!stayIsUpcoming(stay, today)) continue

      const resolution = stay.propertyId ? propertyCountries[stay.propertyId] : undefined
      const countryCode = normalizeRecentSearchHotelCountryCode(stay.countryCode)
        ?? (resolution?.status === 'ready' ? normalizeRecentSearchHotelCountryCode(resolution.countryCode) : undefined)
      const coordinates = stay.coordinates ?? (resolution?.status === 'ready' ? resolution.coordinates : undefined)

      for (const placeId of context.placeIds) {
        const entry = searchPlaces[placeId]
        if (!entry || entry.status === 'loading') {
          pending = true
          continue
        }
        if (entry.status !== 'ready') continue
        const searchCountry = normalizeRecentSearchHotelCountryCode(entry.place.countryCode)
        if (countryCode && searchCountry && countryCode !== searchCountry) continue

        if (entry.place.type === 'country') {
          if (countryCode && countryCode === searchCountry) {
            return { fulfilled: true, pending: false, unresolvedPropertyIds: [], unresolvedBookingLocations: [] }
          }
        } else if (entry.place.type && coordinates) {
          const lookup = { ...coordinates, level: entry.place.type }
          const key = getPlaceKey(lookup.latitude, lookup.longitude, [lookup.level])
          const bookingPlace = bookingPlacesByCoords[key]
          if (bookingPlace?.id === placeId) {
            return { fulfilled: true, pending: false, unresolvedPropertyIds: [], unresolvedBookingLocations: [] }
          }
          if (bookingPlace === undefined) {
            pending = true
            unresolvedBookingLocations.set(key, lookup)
          }
          continue
        } else if (!entry.place.type) {
          continue
        }

        if (stay.propertyId && (!resolution || resolution.status === 'loading')) {
          pending = true
          pushUnique(unresolvedPropertyIds, stay.propertyId)
        }
      }
    }
  }

  return {
    fulfilled: false,
    pending,
    unresolvedPropertyIds,
    unresolvedBookingLocations: Array.from(unresolvedBookingLocations.values()),
  }
}

export function evaluateRecentSearchHotelCarousel(
  input: EvaluateRecentSearchHotelCarouselInput,
): RecentSearchHotelCarouselDecision {
  if (!input.memberId) {
    return decision('suppressed', 'not_logged_in')
  }

  if (input.searches.state === 'error') {
    return decision('suppressed', 'data_error')
  }

  if (
    input.searches.state === 'initial'
    || input.searches.state === 'loading'
    || input.searches.cacheKey !== input.memberId
  ) {
    return decision('loading', 'searches_loading')
  }

  const selected = selectLatestSearch(input.searches.data, input.now, input.today)
  if (selected.kind === 'none') {
    return decision('suppressed', selected.reason)
  }
  if (selected.kind === 'suppressed') {
    return decision('suppressed', selected.reason, selected.context)
  }

  if (!input.ordersFetched) {
    if (input.ordersFailed) {
      return decision('suppressed', 'data_error', selected.context)
    }
    return decision('loading', 'orders_loading', selected.context)
  }

  const fulfilled = evaluateFulfilled(
    selected.context,
    input.purchases,
    input.searchPlaces,
    input.propertyCountries ?? {},
    input.bookingPlacesByCoords ?? {},
    input.today,
  )
  if (fulfilled.fulfilled) {
    return decision('suppressed', 'fulfilled', selected.context)
  }
  if (fulfilled.pending) {
    return decision(
      'loading',
      'fulfilled_unknown',
      selected.context,
      fulfilled.unresolvedPropertyIds,
      fulfilled.unresolvedBookingLocations,
    )
  }

  return decision('loading', 'inventory_pending', selected.context)
}

export function applyRecentSearchHotelInventory(
  current: RecentSearchHotelCarouselDecision,
  inventory: RecentSearchHotelInventoryEvidence,
  requiresExactStay: boolean,
): RecentSearchHotelCarouselDecision {
  if (current.reason !== 'inventory_pending') return current

  const verified = inventory.enabled
    ? inventory.offers.filter(offer => (
        requiresExactStay ? offer.available === true : offer.available !== false
      ))
    : []

  if (verified.length >= RECENT_SEARCH_HOTEL_MIN_OFFERS) {
    return {
      ...decision('eligible', 'eligible', current.context),
      verifiedOfferIds: verified.map(offer => offer.id),
    }
  }

  if (!inventory.enabled || inventory.fetching || !inventory.streamEnded) {
    if (inventory.enabled && inventory.error && !inventory.fetching && inventory.offers.length === 0) {
      return decision('suppressed', 'inventory_error', current.context)
    }
    return current
  }

  if (inventory.error) {
    return decision('suppressed', 'inventory_error', current.context)
  }

  if (inventory.offers.some(offer => offer.available === undefined)) {
    return decision('suppressed', 'data_error', current.context)
  }

  return decision('suppressed', 'no_inventory', current.context)
}

/**
 * Fill the carousel with verified, non-excluded offers while the stream is open.
 * Eligibility still requires only one offer; it does not stop list population.
 */
export function nextRecentSearchHotelOfferBatchSize(input: {
  rawOfferCount: number
  verifiedOfferCount: number
  streamEnded: boolean
  fetching: boolean
  listening: boolean
  hasError: boolean
}): number | undefined {
  if (!input.listening || input.streamEnded || input.fetching || input.hasError) return undefined
  if (input.verifiedOfferCount >= MAX_CAROUSEL_ITEM_COUNT) return undefined
  return input.rawOfferCount + MAX_CAROUSEL_ITEM_COUNT - input.verifiedOfferCount
}

export function buildRecentSearchHotelOfferFilters(
  context: RecentSearchHotelContext,
): App.OfferListFilters {
  return {
    offerTypes: RECENT_SEARCH_HOTEL_OFFER_TYPES,
    destinationIds: context.placeIds,
    rooms: context.rooms,
    limit: MAX_CAROUSEL_ITEM_COUNT,
    isStream: true,
    ...(context.dateMode === 'specific' && context.checkIn && context.checkOut
      ? { checkIn: context.checkIn, checkOut: context.checkOut }
      : {}),
  }
}

export function buildRecentSearchHotelOfferUrlQuery(
  context: RecentSearchHotelContext,
  destinationName: string,
): string {
  const params = buildSearchParamsFromFilters({
    checkIn: context.dateMode === 'specific' ? context.checkIn : undefined,
    checkOut: context.dateMode === 'specific' ? context.checkOut : undefined,
    rooms: context.rooms,
  })
  params.set(queryKeyDestinationId, context.placeIds.join(','))
  params.set(queryKeyDestinationName, destinationName)
  return params.toString()
}

export function buildRecentSearchHotelViewAllUrl(
  context: RecentSearchHotelContext,
  destinationName: string,
): string {
  const filters = buildRecentSearchHotelOfferFilters(context)
  const params = buildSearchParamsFromFilters(filters)
  params.set(queryKeyDestinationId, context.placeIds.join(','))
  params.set(queryKeyDestinationName, destinationName)
  const query = params.toString()
  return query ? `/search?${query}` : '/search'
}

export function buildRecentSearchHotelDatesFilter(
  context: RecentSearchHotelContext,
): App.OfferListFilters | undefined {
  if (context.dateMode !== 'specific' || !context.checkIn || !context.checkOut) return undefined
  return {
    checkIn: context.checkIn,
    checkOut: context.checkOut,
    rooms: context.rooms,
  }
}
